// Feedback Admin Dashboard Page
// Super admin interface for managing feedback conversations

import React, { useState, useMemo } from 'react';
import {
  MessageCircle,
  Clock,
  CheckCircle,
  AlertTriangle,
  Download,
  RefreshCw,
  Archive,
  ArrowDown,
  ArrowUp,
  Minus,
  Search,
  ChevronDown,
  ChevronUp,
  Lightbulb
} from 'lucide-react';
import { Button } from '@vehix/portal/components/ui/button';
import { Card, CardContent } from '@vehix/portal/components/ui/card';
import { Skeleton } from '@vehix/portal/components/ui/skeleton';
import { Input } from '@vehix/portal/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import { cn } from '@vehix/portal/lib/utils';
// Feedback feature stays in portal — only this admin-facing page is migrated.
import { FeedbackList } from '@vehix/portal/features/feedback/components/FeedbackList';
import { ConversationView } from '@vehix/portal/features/feedback/components/ConversationView';
import { useAuth } from '@vehix/shared/admin/portal-bridge';
import {
  useFeedbackAdmin,
  useUpdateConversation,
  useAddMessage,
  useUpdateMessage,
  useDeleteMessage,
  useFeedbackConversation,
  useFeedbackCategories,
  useFeedbackUnread,
  useConversationReadTracking
} from '@vehix/portal/features/feedback/hooks/useFeedback';
import { feedbackService } from '@vehix/portal/features/feedback/services/feedbackService';
import {
  FEATURE_REQUEST_CATEGORY,
  featureRequestBadge,
  isConfirmedFeatureRequest,
  matchesStatusFilter,
  type FeedbackStatusFilter
} from '@vehix/portal/features/feedback/utils/feedbackBuckets';
import type { FeedbackStatus, FeedbackPriority } from '@vehix/portal/features/feedback/types';

// Filter configuration objects
const statusConfig = {
  open: {
    label: 'Open',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: MessageCircle
  },
  in_progress: {
    label: 'In Progress',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Clock
  },
  resolved: {
    label: 'Resolved',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle
  },
  archived: {
    label: 'Archived',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: Archive
  }
};

// Status filter options: real statuses plus the Feature requests section
const statusFilterConfig: Record<
  FeedbackStatusFilter,
  { label: string; color: string; icon: typeof MessageCircle }
> = {
  open: statusConfig.open,
  in_progress: statusConfig.in_progress,
  feature_requests: { ...featureRequestBadge, label: 'Feature requests' },
  resolved: statusConfig.resolved,
  archived: statusConfig.archived
};

const priorityConfig = {
  low: { label: 'Low', color: 'text-gray-900 dark:text-gray-100', icon: ArrowDown },
  medium: { label: 'Medium', color: 'text-blue-700 dark:text-blue-400', icon: Minus },
  high: { label: 'High', color: 'text-orange-700 dark:text-orange-400', icon: ArrowUp },
  urgent: { label: 'Urgent', color: 'text-red-700 dark:text-red-400', icon: AlertTriangle }
};

interface FilterState {
  searchTerm: string;
  statusFilter: FeedbackStatusFilter[];
  priorityFilter: FeedbackPriority[];
  tenantFilter: string;
  submitterFilter: string;
  sortBy: 'created_at' | 'updated_at' | 'priority' | 'status';
  sortOrder: 'asc' | 'desc';
}

export const FeedbackAdminPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedConversationId, setSelectedConversationId] = useState<string>();

  // Data fetching - no server-side filters; filtering is handled in this component
  const {
    stats,
    conversations,
    statsLoading,
    conversationsLoading,
    refetchStats,
    refetchConversations
  } = useFeedbackAdmin({}, { refetchInterval: 60000 });

  // Filter state
  const [filterState, setFilterState] = useState<FilterState>({
    searchTerm: '',
    statusFilter: [],
    priorityFilter: [],
    tenantFilter: '',
    submitterFilter: '',
    sortBy: 'updated_at',
    sortOrder: 'desc'
  });

  // Get unique tenants and submitters for filter dropdowns
  const uniqueTenants = useMemo(() => {
    const tenants = new Set<string>();
    conversations.forEach(conv => {
      if (conv.tenant_name) tenants.add(conv.tenant_name);
    });
    return Array.from(tenants).sort();
  }, [conversations]);

  const uniqueSubmitters = useMemo(() => {
    const submitters = new Map<string, string>();
    conversations.forEach(conv => {
      const id = conv.user_id;
      const name = conv.user_full_name || conv.user_email || 'Unknown';
      if (id && !submitters.has(id)) {
        submitters.set(id, name);
      }
    });
    return Array.from(submitters.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [conversations]);

  // Filter and sort conversations
  const filteredAndSortedConversations = useMemo(() => {
    let filtered = conversations;

    // Search filter
    if (filterState.searchTerm) {
      const searchLower = filterState.searchTerm.toLowerCase();
      filtered = filtered.filter(
        conv =>
          conv.title.toLowerCase().includes(searchLower) ||
          conv.description?.toLowerCase().includes(searchLower) ||
          conv.user_email?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (filterState.statusFilter.length > 0) {
      filtered = filtered.filter(conv =>
        filterState.statusFilter.some(filter => matchesStatusFilter(conv, filter))
      );
    }

    // Priority filter
    if (filterState.priorityFilter.length > 0) {
      filtered = filtered.filter(conv => filterState.priorityFilter.includes(conv.priority));
    }

    // Tenant filter
    if (filterState.tenantFilter) {
      filtered = filtered.filter(conv => conv.tenant_name === filterState.tenantFilter);
    }

    // Submitter filter
    if (filterState.submitterFilter) {
      filtered = filtered.filter(conv => conv.user_id === filterState.submitterFilter);
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      let aValue: number | string = a[filterState.sortBy];
      let bValue: number | string = b[filterState.sortBy];

      // Handle priority sorting with custom order
      if (filterState.sortBy === 'priority') {
        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
        aValue = priorityOrder[a.priority];
        bValue = priorityOrder[b.priority];
      }

      // Handle status sorting with custom order
      if (filterState.sortBy === 'status') {
        const statusOrder = { open: 5, in_progress: 4, resolved: 2, archived: 1 };
        const featureRequestOrder = 3;
        aValue = isConfirmedFeatureRequest(a) ? featureRequestOrder : statusOrder[a.status];
        bValue = isConfirmedFeatureRequest(b) ? featureRequestOrder : statusOrder[b.status];
      }

      // Handle date sorting
      if (filterState.sortBy === 'created_at' || filterState.sortBy === 'updated_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (filterState.sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [conversations, filterState]);

  // Filter handlers
  const handleSearchChange = (value: string) => {
    setFilterState(prev => ({ ...prev, searchTerm: value }));
  };

  const handleSortChange = (sortBy: FilterState['sortBy']) => {
    setFilterState(prev => ({
      ...prev,
      sortBy,
      sortOrder: prev.sortBy === sortBy && prev.sortOrder === 'desc' ? 'asc' : 'desc'
    }));
  };

  const getSortIcon = (column: FilterState['sortBy']) => {
    if (filterState.sortBy !== column) return null;
    return filterState.sortOrder === 'desc' ? (
      <ChevronDown className='h-4 w-4' />
    ) : (
      <ChevronUp className='h-4 w-4' />
    );
  };

  const updateConversationMutation = useUpdateConversation();
  const addMessageMutation = useAddMessage();
  const updateMessageMutation = useUpdateMessage();
  const deleteMessageMutation = useDeleteMessage();
  const { data: categories } = useFeedbackCategories();

  // Fetch the selected conversation with messages
  const { data: selectedConversationWithMessages } =
    useFeedbackConversation(selectedConversationId);

  // Highlight threads with replies this admin has not read; opening one marks it read
  const { unreadCounts } = useFeedbackUnread(user?.id, { refetchInterval: 30000 });
  const { newMessagesSince } = useConversationReadTracking(
    selectedConversationWithMessages,
    user?.id
  );
  const listUnreadCounts = useMemo(
    () =>
      selectedConversationId ? { ...unreadCounts, [selectedConversationId]: 0 } : unreadCounts,
    [unreadCounts, selectedConversationId]
  );

  // Handlers
  const handleConversationSelect = (conversationId: string) => {
    setSelectedConversationId(conversationId);
  };

  const handleStatusUpdate = async (conversationId: string, status: FeedbackStatus) => {
    try {
      await updateConversationMutation.mutateAsync({
        conversationId,
        updates: { status }
      });
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handlePriorityUpdate = async (conversationId: string, priority: FeedbackPriority) => {
    try {
      await updateConversationMutation.mutateAsync({
        conversationId,
        updates: { priority }
      });
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  // Confirm as a feature request: Feature Request category + in progress, then reply to the dealer
  const handleMoveToFeatureRequests = async (note: string) => {
    const conversationId = selectedConversationId;
    if (!conversationId) return;

    const featureRequestCategory = categories?.find(c => c.name === FEATURE_REQUEST_CATEGORY);
    if (!featureRequestCategory) {
      throw new Error('Feature Request category not found');
    }

    await updateConversationMutation.mutateAsync({
      conversationId,
      updates: { category_id: featureRequestCategory.id, status: 'in_progress' }
    });

    if (note) {
      await addMessageMutation.mutateAsync({ conversation_id: conversationId, message: note });
    }
  };

  const handleExportCSV = async () => {
    // TODO: Implement CSV export functionality
    console.log('Export CSV functionality to be implemented');
  };

  const handleTestEmail = async () => {
    try {
      await feedbackService.testEmailNotification();
      // Show success toast
      console.log('Test email sent successfully');
    } catch (error) {
      console.error('Failed to send test email:', error);
      // Show error toast
    }
  };

  const handleRefresh = () => {
    refetchStats();
    refetchConversations();
  };

  const searchIconClass =
    'absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground';

  return (
    <div className='w-full h-full flex flex-col space-y-3 p-6 overflow-hidden'>
      {/* Header Row 1: Title + Action Buttons */}
      <div className='flex items-center justify-between flex-shrink-0'>
        <div className='flex items-start gap-3'>
          <MessageCircle className='h-6 w-6 text-primary mt-1' />
          <div>
            <h1 className='text-3xl font-bold'>Feedback Management</h1>
            <p className='text-muted-foreground text-sm'>
              Manage user feedback and support conversations
            </p>
          </div>
        </div>

        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={handleRefresh}
            disabled={statsLoading || conversationsLoading}
            className='h-8'
          >
            <RefreshCw
              className={cn('h-4 w-4', (statsLoading || conversationsLoading) && 'animate-spin')}
            />
          </Button>

          <Button onClick={handleExportCSV} size='sm' className='h-8'>
            <Download className='h-4 w-4 mr-1' />
            Export
          </Button>

          <Button onClick={handleTestEmail} variant='outline' size='sm' className='h-8'>
            <MessageCircle className='h-4 w-4 mr-1' />
            Test Email
          </Button>
        </div>
      </div>

      {/* Header Row 2: Filters */}
      <div className='flex items-center gap-2 flex-shrink-0'>
        {/* Tenant filter */}
        <Select
          value={filterState.tenantFilter}
          onValueChange={value =>
            setFilterState(prev => ({ ...prev, tenantFilter: value === 'all' ? '' : value }))
          }
        >
          <SelectTrigger className='w-32 h-8 text-xs'>
            <SelectValue placeholder='All Tenants' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Tenants</SelectItem>
            {uniqueTenants.map(tenant => (
              <SelectItem key={tenant} value={tenant}>
                {tenant}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Submitter filter */}
        <Select
          value={filterState.submitterFilter}
          onValueChange={value =>
            setFilterState(prev => ({ ...prev, submitterFilter: value === 'all' ? '' : value }))
          }
        >
          <SelectTrigger className='w-36 h-8 text-xs'>
            <SelectValue placeholder='All Submitters' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Submitters</SelectItem>
            {uniqueSubmitters.map(({ id, name }) => (
              <SelectItem key={id} value={id}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status filter */}
        <Select
          value={filterState.statusFilter.length === 1 ? filterState.statusFilter[0] : 'all'}
          onValueChange={value =>
            setFilterState(prev => ({
              ...prev,
              statusFilter: value === 'all' ? [] : [value as FeedbackStatusFilter]
            }))
          }
        >
          <SelectTrigger
            className={cn(
              'w-32 h-8 text-xs',
              filterState.statusFilter.length === 1 &&
                statusFilterConfig[filterState.statusFilter[0]]?.color
            )}
          >
            <SelectValue placeholder='All Status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Status</SelectItem>
            {Object.entries(statusFilterConfig).map(([status, config]) => (
              <SelectItem key={status} value={status} className={config.color}>
                <div className='flex items-center gap-1.5'>
                  <config.icon className='h-3 w-3' />
                  {config.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Priority filter */}
        <Select
          value={filterState.priorityFilter.length === 1 ? filterState.priorityFilter[0] : 'all'}
          onValueChange={value =>
            setFilterState(prev => ({
              ...prev,
              priorityFilter: value === 'all' ? [] : [value as FeedbackPriority]
            }))
          }
        >
          <SelectTrigger
            className={cn(
              'w-28 h-8 text-xs font-medium',
              filterState.priorityFilter.length === 1 &&
                priorityConfig[filterState.priorityFilter[0]]?.color
            )}
          >
            <SelectValue placeholder='All Priority' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Priority</SelectItem>
            {Object.entries(priorityConfig).map(([priority, config]) => (
              <SelectItem
                key={priority}
                value={priority}
                className={cn('font-medium', config.color)}
              >
                <div className='flex items-center gap-1.5'>
                  <config.icon className='h-3 w-3' />
                  {config.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Search */}
        <div className='relative flex-1 max-w-md'>
          <Search className={searchIconClass} />
          <Input
            placeholder='Search conversations...'
            value={filterState.searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
            className='pl-8 h-8 text-sm'
          />
        </div>

        <span className='text-muted-foreground/40 mx-0.5'>|</span>

        {/* Sort buttons */}
        <span className='text-xs text-muted-foreground'>Sort:</span>
        {[
          { key: 'updated_at' as const, label: 'Updated' },
          { key: 'created_at' as const, label: 'Created' },
          { key: 'priority' as const, label: 'Priority' },
          { key: 'status' as const, label: 'Status' }
        ].map(({ key, label }) => (
          <Button
            key={key}
            variant={filterState.sortBy === key ? 'secondary' : 'ghost'}
            size='sm'
            onClick={() => handleSortChange(key)}
            className='h-6 text-xs px-1'
          >
            {label}
            {getSortIcon(key)}
          </Button>
        ))}
      </div>

      {/* Statistics Cards - Compact layout */}
      <div className='grid grid-cols-2 lg:grid-cols-5 gap-3 flex-shrink-0'>
        <Card>
          <CardContent className='p-3'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-xs font-medium text-muted-foreground'>Total</p>
                {statsLoading ? (
                  <Skeleton className='h-6 w-10 mt-1' />
                ) : (
                  <p className='text-xl font-bold'>{stats?.total_conversations || 0}</p>
                )}
              </div>
              <MessageCircle className='h-6 w-6 text-muted-foreground' />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-3'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-xs font-medium text-muted-foreground'>Open</p>
                {statsLoading ? (
                  <Skeleton className='h-6 w-10 mt-1' />
                ) : (
                  <p className='text-xl font-bold text-blue-600'>
                    {stats?.open_conversations || 0}
                  </p>
                )}
              </div>
              <Clock className='h-6 w-6 text-blue-500' />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-3'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-xs font-medium text-muted-foreground'>In Progress</p>
                {statsLoading ? (
                  <Skeleton className='h-6 w-10 mt-1' />
                ) : (
                  <p className='text-xl font-bold text-yellow-600'>
                    {stats?.in_progress_conversations || 0}
                  </p>
                )}
              </div>
              <AlertTriangle className='h-6 w-6 text-yellow-500' />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-3'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-xs font-medium text-muted-foreground'>Feature requests</p>
                {statsLoading ? (
                  <Skeleton className='h-6 w-10 mt-1' />
                ) : (
                  <p className='text-xl font-bold text-violet-600'>
                    {stats?.feature_request_conversations || 0}
                  </p>
                )}
              </div>
              <Lightbulb className='h-6 w-6 text-violet-500' />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-3'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-xs font-medium text-muted-foreground'>Resolved</p>
                {statsLoading ? (
                  <Skeleton className='h-6 w-10 mt-1' />
                ) : (
                  <p className='text-xl font-bold text-green-600'>
                    {stats?.resolved_conversations || 0}
                  </p>
                )}
              </div>
              <CheckCircle className='h-6 w-6 text-green-500' />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className='grid grid-cols-1 lg:grid-cols-[3fr_7fr] gap-6 flex-1 min-h-0 overflow-hidden'>
        {/* Conversations List */}
        <div className='flex flex-col min-h-0'>
          <div className='flex-1 min-h-0 overflow-y-auto'>
            <FeedbackList
              conversations={filteredAndSortedConversations}
              onConversationSelect={handleConversationSelect}
              onStatusUpdate={handleStatusUpdate}
              onPriorityUpdate={handlePriorityUpdate}
              loading={conversationsLoading}
              unreadCounts={listUnreadCounts}
              canManage={true}
            />
          </div>
        </div>

        {/* Conversation Details */}
        <div className='flex flex-col min-h-0'>
          <div className='flex-1 min-h-0'>
            {selectedConversationWithMessages ? (
              <ConversationView
                conversation={selectedConversationWithMessages}
                currentUserId={user?.id}
                onSendMessage={async data => {
                  if (selectedConversationId) {
                    await addMessageMutation.mutateAsync({
                      conversation_id: selectedConversationId,
                      message: data.message
                    });
                  }
                }}
                onStatusUpdate={status => handleStatusUpdate(selectedConversationId!, status)}
                onPriorityUpdate={priority =>
                  handlePriorityUpdate(selectedConversationId!, priority)
                }
                onEditMessage={async (messageId, newMessage) => {
                  await updateMessageMutation.mutateAsync({
                    messageId,
                    message: newMessage
                  });
                }}
                onDeleteMessage={async messageId => {
                  if (selectedConversationId) {
                    await deleteMessageMutation.mutateAsync({
                      messageId,
                      conversationId: selectedConversationId
                    });
                  }
                }}
                onMoveToFeatureRequests={handleMoveToFeatureRequests}
                {...(newMessagesSince && { newMessagesSince })}
                canManage={true}
              />
            ) : (
              <Card className='h-full'>
                <CardContent className='flex items-center justify-center h-full'>
                  <div className='text-center'>
                    <MessageCircle className='h-12 w-12 mx-auto text-muted-foreground mb-4' />
                    <h3 className='text-lg font-semibold mb-2'>Select a conversation</h3>
                    <p className='text-muted-foreground'>
                      Choose a conversation from the list to view details and manage it.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackAdminPage;
